import {DialogsContext, IconButton, Tooltip, useAnimatedPopper} from 'platform/components';
import {Show} from 'platform/foundation';

import {ReactNode, RefObject, useContext, useId, useRef} from 'react';
import {createPortal} from 'react-dom';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, type Nullish, type TestIdProps, useOutsideClick} from 'shared/web';

import {CustomerInsightsOpenContent} from './components/CustomerInsightsOpenContent';

export interface CustomerInsightsTriggerProps extends TestIdProps {
  customerId: string | Nullish;
  size?: 'small' | 'default';
}

type CustomerInsightsTriggerContentProps = Omit<CustomerInsightsTriggerProps, 'customerId'> & {
  customerId: string;
};

const FLOATING_UI_VIEWPORT_PADDING = 8;

interface CustomerInsightsOpenOutsideClickProps {
  children: ReactNode;
  clickRef?: RefObject<HTMLDivElement | null>;
  enabled: boolean;
  onClose: VoidFunction;
  shouldIgnoreOutsideClick: (target: Element) => boolean;
}

function CustomerInsightsOpenOutsideClick(props: CustomerInsightsOpenOutsideClickProps) {
  const fallbackRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: props.clickRef ?? fallbackRef,
    handler: (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target && props.shouldIgnoreOutsideClick(target)) {
        return;
      }
      props.onClose();
    },
    enabled: props.enabled,
  });

  return props.children;
}

export function CustomerInsightsTrigger(props: CustomerInsightsTriggerProps) {
  const customerId = props.customerId;

  if (isNil(customerId)) {
    return null;
  }

  return <CustomerInsightsTriggerContent key={customerId} {...props} customerId={customerId} />;
}

function CustomerInsightsTriggerContent(props: CustomerInsightsTriggerContentProps) {
  const {isAnyDialogOpen} = useContext(DialogsContext);
  const contentId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const {togglePopper, closePopper, referenceRef, popperProps, Popper, isOpen} = useAnimatedPopper({
    placement: 'bottom-start',
    strategy: 'fixed',
    autoUpdateOptions: 'default',
    // Customer Insights owns outside-click handling only while the portal is mounted.
    isOutsideClickIgnored: true,
    // Floating UI's shift middleware accepts viewport padding only as CSS pixels.
    shiftOptions: {padding: FLOATING_UI_VIEWPORT_PADDING, crossAxis: true},
    shouldIgnoreOutsideClick: (target) =>
      isAnyDialogOpen || (triggerRef.current?.contains(target) ?? false),
  });
  const closeAndRestoreFocus = () => {
    closePopper();
    queueMicrotask(() => triggerRef.current?.focus());
  };

  return (
    <>
      <Tooltip label={i18n.t('feature.customerInsights.title')}>
        <span ref={referenceRef}>
          <IconButton
            ref={triggerRef}
            size={props.size ?? 'small'}
            severity="informational"
            icon="action/history"
            aria-label={i18n.t('feature.customerInsights.title')}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-controls={contentId}
            data-testid={props['data-testid']}
            onClick={togglePopper}
          />
        </span>
      </Tooltip>
      <Show when={isOpen}>
        {createPortal(
          <CustomerInsightsOpenOutsideClick
            clickRef={popperProps.clickRef}
            enabled={!isAnyDialogOpen}
            onClose={closePopper}
            shouldIgnoreOutsideClick={(target) => triggerRef.current?.contains(target) ?? false}
          >
            <Popper {...popperProps} data-testid={suffixTestId('popover', props)}>
              <CustomerInsightsOpenContent
                id={contentId}
                customerId={props.customerId}
                onClose={closeAndRestoreFocus}
              />
            </Popper>
          </CustomerInsightsOpenOutsideClick>,
          document.body
        )}
      </Show>
    </>
  );
}
